import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { Menu, X } from 'lucide-react';
import { useContent } from '../context/ContentContext';

const Navbar: React.FC = () => {
  const { content } = useContent();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsOpen(false);
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  const navLinks = useMemo(() => {
    const links = [
      { name: '首页', href: '#home' },
      { name: '公司简介', href: '#about' },
      { name: '业务范围', href: '#services' },
      { name: '服务流程', href: '#process' },
    ];

    if (content.team?.show !== false) {
      links.push({ name: '专家团队', href: '#team' });
    }

    links.push({ name: '项目案例', href: '#experience' });
    return links;
  }, [content.team?.show]);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-5'}`} role="navigation" aria-label="Main Navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            {content.hero.logoUrl && (
              <img 
                src={content.hero.logoUrl} 
                alt="Jianyun Logo" 
                className={`h-10 md:h-12 w-auto object-contain transition-all duration-300 drop-shadow-md`}
              />
            )}
            <div className={`flex flex-col`}>
              {/* 核心修改：添加 whitespace-nowrap 确保名称单行，使用 fluid typography 确保小屏不溢出 */}
              <div className={`font-bold text-[4.5vw] sm:text-lg md:text-xl tracking-tight transition-colors whitespace-nowrap ${isScrolled ? 'text-slate-900' : 'text-white'}`}>
                {content.hero.companyNameCn.includes('湖南剑云') ? '剑云税务' : content.hero.companyNameCn}
              </div>
              <div className={`hidden md:block text-[8px] uppercase tracking-widest transition-colors whitespace-nowrap ${isScrolled ? 'text-slate-400' : 'text-slate-300 opacity-80'}`}>
                Professional Tax Excellence
              </div>
            </div>
          </div>
          
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`text-sm font-bold tracking-widest uppercase transition-colors hover:text-[#D4AF37] ${isScrolled ? 'text-slate-600' : 'text-slate-100'}`}
              >
                {link.name}
              </a>
            ))}
            <a 
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="bg-[#D4AF37] text-white px-6 py-2.5 rounded-full text-sm font-bold hover:bg-slate-900 transition-all shadow-lg"
              aria-label="在线咨询"
            >
              在线咨询
            </a>
          </div>

          <div className="lg:hidden">
            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className={isScrolled ? 'text-slate-900' : 'text-white'}
              aria-expanded={isOpen}
              aria-label="切换导航菜单"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <div className={`lg:hidden overflow-hidden transition-all duration-300 bg-white shadow-2xl ${isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-6 py-8 space-y-4">
          {navLinks.map((link) => (
            <a key={link.name} href={link.href} onClick={(e) => scrollToSection(e, link.href)} className="block text-lg font-bold text-slate-700 hover:text-[#D4AF37]">
              {link.name}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default memo(Navbar);